import { TFunction } from 'i18next'
import { formatPrice } from 'src/helpers/formatPrice'

export interface ListingFactsParameters {
  rooms?: number | string | null
  floor?: number | string | null
  totalFloors?: number | string | null
  totalArea?: number | string | null
  // a plot's purpose as its code, e.g. "residential"
  landPurpose?: string | null
  // the kind of a commercial property, e.g. "office_space"
  commercialType?: string | null
}

export interface ListingFactsInput {
  propertyType?: string | null
  listingType?: string | null
  price?: { amount: number; currency: string } | null
  parameters?: ListingFactsParameters | null
}

const BUILDINGS = ['HOUSE', 'TOWNHOUSE', 'DUPLEX', 'PART_OF_HOUSE']

const positive = (value: unknown): number | undefined => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

// The short facts line of a listing card: "2 кімн. · 54 м² · 5/9 пов.". It is
// always shown — the facts used to hide in a hover overlay that phones never
// open. A plot or a garage has no rooms; a house tells its storeys, a flat
// its floor.
export const getListingFacts = (
  t: TFunction,
  { propertyType, parameters }: ListingFactsInput,
): string[] => {
  const params = parameters || {}
  const facts: string[] = []
  const type = propertyType ?? ''
  const roomsNumber = positive(params.rooms)
  // "5+" and the like come as text
  const rooms =
    roomsNumber ??
    (typeof params.rooms === 'string' && params.rooms.trim() ? params.rooms.trim() : undefined)
  if (rooms !== undefined && type !== 'PLOT' && type !== 'GARAGE') {
    facts.push(t('card.rooms', { value: rooms }))
  }
  const area = positive(params.totalArea)
  if (area) facts.push(t('card.area', { value: area }))
  const floor = positive(params.floor)
  const totalFloors = positive(params.totalFloors)
  if (BUILDINGS.includes(type)) {
    if (totalFloors) facts.push(t('card.storeys', { count: totalFloors }))
  } else if (type !== 'PLOT') {
    if (floor && totalFloors) facts.push(t('card.floor_of', { floor, total: totalFloors }))
    else if (floor) facts.push(t('card.floor', { floor }))
  }
  // A plot tells what it may be used for (owner feedback 2026-08-21: in the
  // site's language, never the code); an unknown code adds nothing
  if (type === 'PLOT' && params.landPurpose) {
    const purpose = t(`step3.landPurpose_${String(params.landPurpose).toLowerCase()}`, {
      defaultValue: '',
    })
    if (purpose) facts.push(purpose)
  }
  // …and a commercial property its kind (office, shop, warehouse…)
  if (type === 'COMMERCIAL_PROPERTY' && params.commercialType) {
    const kind = t(`step1.${String(params.commercialType).toLowerCase()}`, { defaultValue: '' })
    if (kind) facts.push(kind)
  }
  return facts
}

// "1 574 $/м²" — only for a sale with a known area.
export const getPricePerSquareMeter = (
  t: TFunction,
  { listingType, price, parameters }: ListingFactsInput,
): string | undefined => {
  const area = positive(parameters?.totalArea)
  if (listingType !== 'SALE' || !price || !area || !(price.amount > 0)) return undefined
  return t('card.per_m2', { value: formatPrice(Math.round(price.amount / area), price.currency) })
}
